import React from "react";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import buildTheme from "./Theme";
import "./App.css";
import "./accessibility.css";
import { UserDetailsProvider } from "./Context/userDetailsContext";
import { AccessibilityProvider } from "./Context/AccessibilityContext";
import { ChatbotProvider } from "./Context/ChatbotContext";
import { NavigationProvider } from "./Context/NavigationContext";
import MainApp from "./pages/MainApp";
import GlobalStyles from "./GlobalStyles";
import AccessibilityToolbar from "./Components/AccessibilityToolbar";
import SkipToMain from "./Components/SkipToMain";
import { ChatbotButton } from "./Components/Chatbot";
import FloatingAccessibilityButton from "./Components/FloatingAccessibilityButton";
import { useContext, useEffect, useState } from "react";
import { ConfigLinksDetailsContext } from "./Context/cofingLinksDetailsContext";
import { ConfigLinksDetailsProvider } from "./Context/cofingLinksDetailsContext";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./appInsights";
import ErrorBoundary from "./Components/ErrorBoundary";
import SEO from "./Components/SEO";
import i18n from "./i18n";

// Create RTL and LTR emotion caches
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});
const cacheLtr = createCache({
  key: "muiltr",
  stylisPlugins: [prefixer],
});

const ThemedAppContent = () => {
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const primaryColor = configLinks?.primaryColor || "#BC1F41";
  const chatbotEnabled =
    configLinks?.municipalitySettings?.chatbotEnabled ?? true;

  // Track language for RTL/LTR switching
  const savedLang = localStorage.getItem("i18nextLng") || "ar";
  const [lang, setLang] = useState(savedLang);
  const direction = lang === "ar" ? "rtl" : "ltr";
  const theme = buildTheme(primaryColor, direction);

  useEffect(() => {
    // Set initial direction
    document.documentElement.dir = direction;
    document.documentElement.lang = lang;

    // Subscribe to language changes
    const handleLangChange = (lng) => {
      // Normalize language code (e.g. 'en-US' -> 'en')
      const normalizedLng = lng.startsWith('ar') ? 'ar' : 'en';
      setLang(normalizedLng);
      const dir = normalizedLng === "ar" ? "rtl" : "ltr";
      document.documentElement.dir = dir;
      document.documentElement.lang = normalizedLng;
    };
    i18n.on("languageChanged", handleLangChange);
    return () => i18n.off("languageChanged", handleLangChange);
  }, []);

  return (
    <CacheProvider value={direction === "rtl" ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <SEO />
        <AppInsightsContext.Provider value={reactPlugin}>
          <ErrorBoundary>
            <NavigationProvider>
              <AccessibilityProvider>
                <ChatbotProvider>
                  <SkipToMain />
                  <MainApp />
                  {/* Keep toolbar drawers but suppress its built-in floating button */}
                  <AccessibilityToolbar showFloatingButton={false} />
                  {/* Floating buttons */}
                  <FloatingAccessibilityButton />
                  {chatbotEnabled && <ChatbotButton />}
                </ChatbotProvider>
              </AccessibilityProvider>
            </NavigationProvider>
          </ErrorBoundary>
        </AppInsightsContext.Provider>
      </ThemeProvider>
    </CacheProvider>
  );
};

function App() {
  return (
    <div className="App">
      <ConfigLinksDetailsProvider>
        <UserDetailsProvider>
          <ThemedAppContent />
        </UserDetailsProvider>
      </ConfigLinksDetailsProvider>
    </div>
  );
}

export default App;
